import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  FormControlLabel,
  Switch,
  Button,
  Alert,
  Typography,
  Chip,
  Paper,
  IconButton,
  InputAdornment,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Save as SaveIcon,
  Send as SendIcon,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../../config/apiConfig';
import { log } from '../../utils/errors/logger';

interface SMTPConfig {
  source: 'database' | 'environment' | 'error';
  config: {
    smtp_host: string;
    smtp_port: number;
    smtp_user: string;
    smtp_password: string;
    smtp_from_email: string;
    smtp_use_tls: boolean;
    smtp_from_name: string;
    migrated_from_env?: boolean;
    migrated_at?: string;
    created_by?: string;
    updated_by?: string;
  };
  error?: string;
}

interface MailSettingsProps {
  onUpdate?: () => void;
}

export default function MailSettings({ onUpdate }: MailSettingsProps) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [smtpConfig, setSmtpConfig] = useState<SMTPConfig | null>(null);
  const [testEmail, setTestEmail] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [alert, setAlert] = useState<{ type: 'success' | 'error' | 'warning' | 'info'; message: string } | null>(null);

  useEffect(() => {
    loadSmtpConfig();
  }, []);

  const loadSmtpConfig = async () => {
    try {
      setLoading(true);
      const response = await apiClient.getSmtpConfigApiAdminSmtpConfigGet();
      setSmtpConfig(response.data as SMTPConfig);
    } catch (error) {
      log.error('Error loading SMTP config', { error });
      setAlert({ type: 'error', message: t('system.mail.loadError') });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSmtpConfig = async () => {
    if (!smtpConfig?.config) {return;}

    try {
      setLoading(true);
      await apiClient.saveSmtpConfigApiAdminSmtpConfigPost(smtpConfig.config);
      setAlert({ type: 'success', message: t('system.mail.saveSuccess') });
      await loadSmtpConfig();
      onUpdate?.();
    } catch (error) {
      log.error('Error saving SMTP config', { error });
      setAlert({ type: 'error', message: t('system.mail.saveError') });
    } finally {
      setLoading(false);
    }
  };

  const handleTestEmail = async () => {
    if (!testEmail) {
      setAlert({ type: 'warning', message: t('system.mail.testEmailRequired') });
      return;
    }

    try {
      setLoading(true);
      await apiClient.testSmtpConfigApiAdminSmtpTestPost({ 
        email: testEmail 
      });
      setAlert({ type: 'success', message: t('system.mail.testSuccess') });
    } catch (error) {
      log.error('Error sending test email', { error });
      setAlert({ type: 'error', message: t('system.mail.testError') });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof SMTPConfig['config']) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (!smtpConfig) {return;}
    
    const value = field === 'smtp_port' 
      ? parseInt(event.target.value, 10) 
      : field === 'smtp_use_tls'
        ? event.target.checked
        : event.target.value;

    setSmtpConfig({
      ...smtpConfig,
      config: {
        ...smtpConfig.config,
        [field]: value,
      },
    });
  };

  if (!smtpConfig) {
    return null;
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        {t('system.mail.title')}
      </Typography>
      
      <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
        <Chip
          label={
            smtpConfig.source === 'environment'
              ? t('system.mail.sourceEnvironment')
              : smtpConfig.source === 'database'
              ? t('system.mail.sourceDatabase')
              : t('system.mail.sourceError')
          }
          color={smtpConfig.source === 'error' ? 'error' : 'primary'}
          size="small"
        />
        {smtpConfig.source === 'database' && smtpConfig.config.migrated_from_env && (
          <Chip
            label={t('system.mail.migratedFromEnvironment')}
            color="info"
            size="small"
            variant="outlined"
          />
        )}
      </Box>

      {alert && (
        <Alert severity={alert.type} sx={{ mb: 3 }} onClose={() => setAlert(null)}>
          {alert.message}
        </Alert>
      )}

      {smtpConfig.error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {smtpConfig.error}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom>
          {t('system.mail.serverSettings')}
        </Typography>
        
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 8 }}>
            <TextField
              fullWidth
              label={t('system.mail.smtpHost')}
              value={smtpConfig.config.smtp_host || ''}
              onChange={handleInputChange('smtp_host')}
              disabled={loading || smtpConfig.source === 'environment'}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth
              type="number"
              label={t('system.mail.smtpPort')}
              value={smtpConfig.config.smtp_port || 587}
              onChange={handleInputChange('smtp_port')}
              disabled={loading || smtpConfig.source === 'environment'}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label={t('system.mail.smtpUser')}
              value={smtpConfig.config.smtp_user || ''}
              onChange={handleInputChange('smtp_user')}
              disabled={loading || smtpConfig.source === 'environment'}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              type={showPassword ? 'text' : 'password'}
              label={t('system.mail.smtpPassword')}
              value={smtpConfig.config.smtp_password || ''}
              onChange={handleInputChange('smtp_password')}
              disabled={loading || smtpConfig.source === 'environment'}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid size={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={smtpConfig.config.smtp_use_tls || false}
                  onChange={handleInputChange('smtp_use_tls')}
                  disabled={loading || smtpConfig.source === 'environment'}
                />
              }
              label={t('system.mail.useTls')}
            />
          </Grid>
        </Grid>
      </Paper>

      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom>
          {t('system.mail.senderSettings')}
        </Typography>
        
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label={t('system.mail.fromName')}
              value={smtpConfig.config.smtp_from_name || ''}
              onChange={handleInputChange('smtp_from_name')}
              disabled={loading || smtpConfig.source === 'environment'}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              type="email"
              label={t('system.mail.fromEmail')}
              value={smtpConfig.config.smtp_from_email || ''}
              onChange={handleInputChange('smtp_from_email')}
              disabled={loading || smtpConfig.source === 'environment'}
            />
          </Grid>
        </Grid>
      </Paper>

      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom>
          {t('system.mail.testSettings')}
        </Typography>
        
        <Grid container spacing={3} alignItems="center">
          <Grid size={{ xs: 12, md: 8 }}>
            <TextField
              fullWidth
              type="email"
              label={t('system.mail.testEmailAddress')}
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              disabled={loading}
              placeholder="test@example.com"
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<SendIcon />}
              onClick={handleTestEmail}
              disabled={loading || !testEmail}
            >
              {t('system.mail.sendTestEmail')}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSaveSmtpConfig}
          disabled={loading || smtpConfig.source === 'environment'}
        >
          {t('system.mail.save')}
        </Button>
      </Box>
    </Box>
  );
}
